import {
  type StoreEnhancer,
  combineReducers,
  configureStore,
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  createAsyncThunk as createAsyncThunkRedux,
  createListenerMiddleware,
  isPlain,
} from '@reduxjs/toolkit';
import type { EnhancedStore, ReducersMapObject, ThunkDispatch, Tuple, UnknownAction } from '@reduxjs/toolkit';
import type { Middleware } from 'redux';
import dynamicMiddlewares from 'redux-dynamic-middlewares';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { addMiddleware as dynamicMiddlewareAddMiddleware, removeMiddleware } from 'redux-dynamic-middlewares';

import { assert, assertNonNullable } from '@disy/lib/custom-error';
import { isDebug } from '@disy/lib/disy';
import { logger } from '@disy/lib/logging';
import type { Teardown } from '@disy/lib/teardown';

import { Command } from 'cadenza/api-client/workbook-commands';

// Timeline slices can be very big as they include the timelines with all points. Because this
// is too much for the developer supporting middlewares (not active in production) to handle
// gracefully, we let the middlewares ignore the timelines in the timeline slice.
const ignoredPaths = ['timelineSlice.timelines'];

// Do not export these. Use getStore() instead.
let store = createCadenzaStore();
let secondaryStore: CadenzaStore | undefined;

export type CadenzaStore = ReturnType<typeof createCadenzaStore>;

/* Basis state containing the individual slices. Latter are defined via augmentation in the individual modules. */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface RootState {
  // Nothing to do here
}

type SlicePrefix = keyof RootState;

export type RootStateOr<S extends RootState[SlicePrefix]> = RootState | S;

function createCadenzaStore({
  reducers = {},
  preloadedState,
}: {
  reducers?: Partial<ReducersMapObject<RootState>>;
  preloadedState?: RootState;
} = {}) {
  // Listener middleware: allows attaching side effects to actions independently of state change
  // Makes actions more "event-like"
  // https://redux-toolkit.js.org/api/createListenerMiddleware
  const listenerMiddleware = createListenerMiddleware();

  const newStore: EnhancedStore<
    RootState,
    UnknownAction,
    Tuple<[StoreEnhancer<{ dispatch: ThunkDispatch<RootState, undefined, UnknownAction> }>, StoreEnhancer]>
  > = configureStore({
    /*
     * Actually, we never have reducers for all slices registered, because most of them are
     * page-specific. But on a given page, all relevant reducers must be registered.
     * So for simplicity, we pretend that all the reducers are always registered.
     */
    reducer:
      Object.keys(reducers).length ? combineReducers(reducers as ReducersMapObject<RootState>) : (state) => state!,
    // Customize the serializableCheck middleware to allow Date instances as serializable.
    // See https://redux-toolkit.js.org/api/getDefaultMiddleware and https://redux-toolkit.js.org/api/serializabilityMiddleware
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // See: https://redux-toolkit.js.org/api/serializabilityMiddleware
          isSerializable: (value: unknown) => isPlain(value) || value instanceof Date || value instanceof Command,
          ignoredPaths,
        },
        immutableCheck: {
          // See: https://redux-toolkit.js.org/api/immutabilityMiddleware
          ignoredPaths,
        },
      })
        .prepend(listenerMiddleware.middleware)
        // dynamicMiddlewares allows for adding and removing middleware after store is created
        // https://github.com/pofigizm/redux-dynamic-middlewares
        .concat(dynamicMiddlewares),
    preloadedState,
  });

  // Stored to override and reuse below
  const originalDispatch = newStore.dispatch;
  // Clone the reducers object to not mutate the argument.
  reducers = { ...reducers };

  return Object.assign(newStore, {
    registerReducer<Slice extends SlicePrefix>(name: Slice, reducer: ReducersMapObject<RootState>[Slice]) {
      reducers[name] = reducer;
      newStore.replaceReducer(combineReducers(reducers as ReducersMapObject<RootState>));
    },
    // Here for testing purposes only.
    get __reducers__() {
      return reducers;
    },
    dispatch(action: UnknownAction, ...extraArgs: unknown[]) {
      /*
        Warn about actions being dispatched in observerStore() callbacks, as this will lead to unnecessary observer
        callbacks called and can lead to unnecessary UI updates and even inconsistent model/UI states (partly old
        and new state used) until the final nested dispatch() as been executed.
        In most situations the correct approach would be to update the state original action reducer or an
        extraReducer, so that all state changes are applied before observer callbacks are called.
        See: https://redux.js.org/api/store#subscribelistener
       */
      const callStackDepth = this.observeStoreCallbackCallStackDepth;
      if (isDebug() && callStackDepth > 0) {
        const typeInfo = action.type ? ` ${action.type}` : '';
        logger.warn(
          `Action${typeInfo} dispatched inside of an observeStore() callback (nested in ${callStackDepth} callbacks).`,
        );
      }

      // Invoke original dispatch()
      return originalDispatch(action, ...extraArgs);
    },
    observeStoreCallbackCallStackDepth: 0,
  });
}

/**
 * Initializes a temporary "fork" from the main store, which may or may not be reconciled later with the main store.
 *
 * {@link getStore()} returns this secondary store if it is available, i.e.
 * selectors get the state from it and actions are dispatched on it.
 * When the secondary store is not needed anymore, it must be cleared using {@link clearSecondaryStore}.
 *
 * @param reducers - Reducers that should be initially registered on the secondary store;
 *   Reducers that are registered later will not get the state from the main store!
 * @see https://redux.js.org/usage/structuring-reducers/initializing-state#summary
 */
export function initSecondaryStore(reducers: Partial<ReducersMapObject<RootState>>) {
  assert(secondaryStore == null, 'There is a secondary store already');
  assert(
    store.getState()?.workbookSlice.commandStatus !== 'pending',
    'Secondary store cannot be initialized while main store is being updated',
  );
  secondaryStore = createCadenzaStore({ preloadedState: store.getState(), reducers });
}

/**
 * Terminates the secondary store.
 */
export function clearSecondaryStore() {
  secondaryStore = undefined;
}

// Exported for testing purposes only.
export function __resetPrimaryStore__() {
  store = createCadenzaStore({ reducers: store.__reducers__ });
}

/**
 * Returns the secondary store if it is available, otherwise the main store.
 */
export function getStore() {
  return secondaryStore ?? store;
}

/**
 * Returns the primary store, regardless of the existence of a secondary store.
 */
export function getPrimaryStore() {
  return store;
}

/**
 * Returns the secondary store. If it is not available, throws an error.
 * Use this function when the secondary store is specifically needed.
 */
export function getSecondaryStore() {
  assertNonNullable(secondaryStore, 'Secondary state was requested explicitly while it is not active.');
  return secondaryStore;
}

export function hasSecondaryStore() {
  return secondaryStore != null;
}

export function createSliceFunction<S extends SlicePrefix>(slicePrefix: S) {
  return (state: RootStateOr<RootState[S]>) => (state as RootState)[slicePrefix] ?? state;
}

/*
 * Adds a dynamic middleware, active for both primary and secondary store.
 */
export function addMiddleware(middleware: Middleware): Teardown {
  dynamicMiddlewareAddMiddleware(middleware);
  return () => removeMiddleware(middleware);
}

export const createAsyncThunk = createAsyncThunkRedux.withTypes<{ state: RootState }>();
